import { useMutation } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';

import { buildJsonLogicRule } from '../../../common/buildJsonLogic';
import { injectAutotags } from '../developmentAutoTagging';

import type { Volume } from '~/shared/types/api';
import type { Condition } from '../../useAutoscaleRulesBuilder';

export interface DryRunVolumeTag {
  name: string;
  value: string;
}

export interface VolumeCategory {
  volumes: Volume[];
  count: number;
  totalCapacity: number;
  averageUtilization: number;
}

export interface DryRunResponse {
  datafiable: VolumeCategory;
  potentiallyDatafiable: VolumeCategory;
  excluded: VolumeCategory;
}

interface DryRunRequestBody {
  active: boolean;
  rule: Record<string, unknown>;
}

export function useDryRunMutation() {
  const api = useXFetch();
  const { user, accounts } = useAuthContext();

  return useMutation({
    mutationFn: async ({
      conditions,
      accountId,
    }: {
      conditions: Condition[];
      accountId?: string;
    }) => {

      if (conditions.length === 0) {
        throw new Error('No rules to process');
      }

      const rulesWithAutotags = injectAutotags(conditions, user?.email, accounts);

      const conditionJsonLogicRules = rulesWithAutotags
        .map((rule) => buildJsonLogicRule(rule))
        .filter(
          (logic): logic is Record<string, unknown> | Record<string, unknown>[] => logic !== null
        )
        .flat();

      if (conditionJsonLogicRules.length === 0) {
        throw new Error('No valid rules to process');
      }

      const jsonLogicRule =
        conditionJsonLogicRules.length === 1
          ? conditionJsonLogicRules[0]
          : { and: conditionJsonLogicRules };

      const requestBody: DryRunRequestBody = {
        active: true,
        rule: jsonLogicRule,
      };

      let url = '/api/v1/volumes/autoscaling/rules/dry-run';
      if (accountId) {
        url += `?accounts=${accountId}`;
      }

      const response = await api<DryRunResponse>(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      return response;
    },
  });
}
